import { useMemo } from 'react';
import { useGetChatDocumentInfoQuery } from '@/api';
import type { ChatDocument } from '@/store/slices/chat/chat';
import type { Citation } from '@/types/chat';
import { FileStatus } from '@/types/file';
import { getUniqueCitationDocumentIds } from '@/ui/pages/ChatPage/utils/getUniqueCitationDocumentIds';

interface UseSourcesPanelDocumentsParams {
  chatId: string | undefined;
  citations: (Citation & { id: string })[];
  chatDocuments: ChatDocument[];
}

/**
 * Whole-case chats cite documents missing from `/chat/documents`, so get their names from
 * `/chat/document-info` and merge them in.
 */
export const useSourcesPanelDocuments = ({
  chatId,
  citations,
  chatDocuments,
}: UseSourcesPanelDocumentsParams): ChatDocument[] => {
  const citationDocumentIds = useMemo(() => getUniqueCitationDocumentIds(citations), [citations]);

  const knownDocumentIds = useMemo(
    () => new Set(chatDocuments.map((document) => document.documentId)),
    [chatDocuments],
  );

  // Only resolve names for cited documents we don't already have locally; the
  // uploaded chat documents already carry richer metadata.
  const missingDocumentIds = useMemo(
    () => citationDocumentIds.filter((documentId) => !knownDocumentIds.has(documentId)),
    [citationDocumentIds, knownDocumentIds],
  );

  const { data } = useGetChatDocumentInfoQuery(
    { chatId: chatId ?? '', documentIds: missingDocumentIds },
    { skip: !chatId || missingDocumentIds.length === 0 },
  );

  return useMemo(() => {
    const documentInfo = data?.documentInfo;
    if (!documentInfo) return chatDocuments;

    const resolvedDocuments = missingDocumentIds.reduce<ChatDocument[]>((documents, documentId) => {
      const entry = documentInfo[documentId];
      if (entry) {
        documents.push({
          documentId,
          documentName: entry.name,
          status: FileStatus.UPLOADED,
          isDisplayable: entry.isDisplayable,
        });
      }
      return documents;
    }, []);

    if (resolvedDocuments.length === 0) return chatDocuments;

    return [...chatDocuments, ...resolvedDocuments];
  }, [chatDocuments, data?.documentInfo, missingDocumentIds]);
};
